/**
 * AdminUnmappedConditions.jsx — iter899 (Feb 2026)
 *
 * Lists auto-diagnosis conditions that fired without a KEGG pathway
 * mapping. Admin either assigns pathway codes (soft mapping — engineers
 * promote to code next release) or dismisses the entry.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, RefreshCw, GitBranch } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const STATUS_TABS = [
  { key: "pending",   label: "Чакащи" },
  { key: "mapped",    label: "Съпоставени" },
  { key: "dismissed", label: "Отхвърлени" },
  { key: "all",       label: "Всички" },
];

export default function AdminUnmappedConditions() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [drafts, setDrafts] = useState({});   // condition_id → {pathways, note}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/unmapped-conditions`, { params: { status } });
      setItems(data.conditions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при зареждане");
    } finally { setLoading(false); }
  }, [API, status]);

  useEffect(() => { load(); }, [load]);

  const setDraft = (id, patch) => setDrafts(d => ({ ...d, [id]: { ...d[id], ...patch } }));

  const doMap = async (item) => {
    setBusyId(item.condition_id);
    try {
      const d = drafts[item.condition_id] || {};
      const pathways = (d.pathways || "")
        .split(/[,\s]+/)
        .map(s => s.trim())
        .filter(Boolean);
      if (!pathways.length) {
        toast.error("Въведете поне един pathway код (например 'estrogen_signaling')");
        return;
      }
      await axios.post(`${API}/admin/unmapped-conditions/${item.condition_id}/map`, {
        pathways,
        note: d.note || undefined,
      });
      toast.success("Съпоставено");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setBusyId(null); }
  };

  const doDismiss = async (item) => {
    setBusyId(item.condition_id);
    try {
      const d = drafts[item.condition_id] || {};
      await axios.post(`${API}/admin/unmapped-conditions/${item.condition_id}/dismiss`, {
        reason: d.note || undefined,
      });
      toast.success("Отхвърлено");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setBusyId(null); }
  };

  const rowCls = isDark
    ? "border-slate-700 bg-slate-900/50"
    : "border-slate-200 bg-white";

  return (
    <div data-testid="admin-unmapped-conditions-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-emerald-500" />
          <h2 className="text-base font-bold">Незасечени KEGG пътища</h2>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
            isDark ? "bg-slate-800 hover:bg-slate-700 text-slate-200" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
          }`}
          data-testid="admin-unmapped-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Опресни
        </button>
      </div>

      <p className={`text-xs mb-3 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
        Тук се събират условия, задействани от auto-diagnosis правила, но без KEGG mapping. Съпоставете ги с pathways
        (например: <code>estrogen_signaling, HPA_axis</code>) за да бъдат промотирани в кода при следващата версия.
      </p>

      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        {STATUS_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
              status === t.key
                ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
                : isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
            }`}
            data-testid={`admin-unmapped-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin opacity-40" />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className={`p-6 rounded-xl border text-center text-sm ${rowCls}`}>
          Няма условия със статус „{status}“.
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => {
          const isPending = item.status === "pending";
          const d = drafts[item.condition_id] || {};
          return (
            <div
              key={item.condition_id}
              className={`p-4 rounded-xl border ${rowCls}`}
              data-testid={`admin-unmapped-row-${item.condition_id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-xs font-mono font-bold">{item.condition_id}</code>
                    {item.name_bg && (
                      <span className="text-xs opacity-70">— {item.name_bg}</span>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      item.status === "pending"   ? "bg-amber-500/20 text-amber-500" :
                      item.status === "mapped"    ? "bg-emerald-500/20 text-emerald-500" :
                                                    "bg-slate-500/20 text-slate-500"
                    }`}>{item.status}</span>
                  </div>
                  <div className="text-[11px] opacity-60 mt-1">
                    Първо срещане: {item.first_seen ? new Date(item.first_seen).toLocaleDateString("bg") : "—"} ·
                    Последно: {item.last_seen ? new Date(item.last_seen).toLocaleDateString("bg") : "—"} ·
                    Пъти: <b>{item.times_triggered || 0}</b> ·
                    Уники потребители: <b>{(item.sample_user_ids || []).length}</b>
                  </div>
                </div>
              </div>

              {Array.isArray(item.sample_evidence) && item.sample_evidence.length > 0 && (
                <div className={`text-[11px] p-2 rounded-lg mb-3 ${isDark ? "bg-slate-800" : "bg-slate-50"}`}>
                  <div className="opacity-70 mb-1">Примерни доказателства:</div>
                  {item.sample_evidence.slice(-2).map((row, i) => (
                    <div key={i} className="font-mono opacity-80">
                      {(row.evidence || []).slice(0, 3).map(e =>
                        `${e.code}: ${typeof e.value === "number" ? e.value.toFixed(2) : e.value}`
                      ).join(", ")}
                    </div>
                  ))}
                </div>
              )}

              {item.mapped_pathways && item.mapped_pathways.length > 0 && (
                <div className="text-xs mb-2">
                  <b>Съпоставени пътища:</b>{" "}
                  <span className="font-mono">{item.mapped_pathways.join(", ")}</span>
                </div>
              )}

              {isPending && (
                <>
                  <input
                    type="text"
                    placeholder="Pathway кодове (например: estrogen_signaling, HPA_axis)"
                    value={d.pathways || ""}
                    onChange={(e) => setDraft(item.condition_id, { pathways: e.target.value })}
                    className={`w-full text-xs p-2 rounded-lg border mb-2 ${
                      isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"
                    }`}
                    data-testid={`admin-unmapped-pathways-${item.condition_id}`}
                  />
                  <input
                    type="text"
                    placeholder="Бележка (по избор)"
                    value={d.note || ""}
                    onChange={(e) => setDraft(item.condition_id, { note: e.target.value })}
                    className={`w-full text-xs p-2 rounded-lg border mb-2 ${
                      isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"
                    }`}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => doMap(item)}
                      disabled={busyId === item.condition_id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                      data-testid={`admin-unmapped-map-${item.condition_id}`}
                    >
                      {busyId === item.condition_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                      Съпостави
                    </button>
                    <button
                      onClick={() => doDismiss(item)}
                      disabled={busyId === item.condition_id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-500 text-white hover:bg-slate-600 disabled:opacity-60"
                      data-testid={`admin-unmapped-dismiss-${item.condition_id}`}
                    >
                      <XCircle className="w-3 h-3" />
                      Отхвърли
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
